import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { isManager, isDeveloper, isAdmin } from '../utils/role';
import Avatar from '../components/Avatar';

const TASK_STATUSES = ['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'DONE', 'BLOCKED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const statusColor = (s) => ({ TODO: 'badge-gray', IN_PROGRESS: 'badge-info', IN_REVIEW: 'badge-warning', DONE: 'badge-success', BLOCKED: 'badge-danger' }[s] || 'badge-gray');
const priorityColor = (p) => ({ CRITICAL: 'badge-danger', HIGH: 'badge-warning', MEDIUM: 'badge-info', LOW: 'badge-gray' }[p] || 'badge-gray');
const statusLabel = { TODO: 'To Do', IN_PROGRESS: 'In Progress', IN_REVIEW: 'In Review', DONE: 'Done', BLOCKED: 'Blocked' };
const statusBorderColor = { TODO: '#6b7280', IN_PROGRESS: '#1a56db', IN_REVIEW: '#d97706', DONE: '#059669', BLOCKED: '#dc2626' };

export default function TaskDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showSubtaskForm, setShowSubtaskForm] = useState(false);
  const [subtaskForm, setSubtaskForm] = useState({ title: '', assigneeId: '', assigneeUsername: '', assigneeName: '', priority: 'MEDIUM', dueDate: '' });
  const [editingSubtask, setEditingSubtask] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const loadAttachments = async () => {
    try {
      const r = await api.get(`/attachments?entityType=TASK&entityId=${taskId}`);
      setAttachments(r.data);
    } catch {}
  };

  const uploadFile = async (e) => {
    const file = e.target.files[0];
    if (!file || !task) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entityType', 'TASK');
      formData.append('entityId', taskId);
      formData.append('projectId', task.projectId);
      await api.post('/attachments/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadAttachments();
    } catch (err) { alert(err.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const downloadAttachment = async (att) => {
    try {
      const r = await api.get(`/attachments/${att.id}/download`);
      window.open(r.data.url, '_blank');
    } catch (err) { alert('Download failed'); }
  };

  const deleteAttachment = async (att) => {
    if (!window.confirm(`Delete "${att.fileName}"?`)) return;
    try {
      await api.delete(`/attachments/${att.id}`);
      loadAttachments();
    } catch (err) { alert(err.response?.data?.error || 'Delete failed'); }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const getFileIcon = (name) => {
    const ext = (name || '').split('.').pop().toLowerCase();
    if (['jpg','jpeg','png','gif','svg','webp'].includes(ext)) return '🖼️';
    if (['pdf'].includes(ext)) return '📕';
    if (['doc','docx'].includes(ext)) return '📘';
    if (['xls','xlsx','csv'].includes(ext)) return '📗';
    if (['zip','rar','7z','tar','gz'].includes(ext)) return '📦';
    if (['mp4','mov','avi','mkv'].includes(ext)) return '🎬';
    return '📄';
  };

  const loadTask = async () => {
    try {
      const r = await api.get(`/tasks/${taskId}/subtasks`);
      setSubtasks(r.data);
    } catch {}
  };

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const taskR = await api.get(`/tasks/${taskId}`);
        const t = taskR.data;
        setTask(t);
        setForm(t);
        const [projR, ms, pm] = await Promise.all([
          api.get(`/projects/${t.projectId}`),
          api.get(`/projects/${t.projectId}/milestones`).catch(() => ({ data: [] })),
          api.get(`/projects/${t.projectId}/members`).catch(() => ({ data: [] }))
        ]);
        setProject(projR.data);
        setMilestones(ms.data);
        setProjectMembers(pm.data.filter(m => m.status === 'ACTIVE'));
      } catch { navigate('/'); }
    };
    fetchAll();
    loadTask();
    loadAttachments();
  }, [taskId]);

  const saveTask = async () => {
    await api.put(`/tasks/${taskId}`, form);
    setTask({ ...task, ...form });
    setEditing(false);
  };

  const updateStatus = async (newStatus) => {
    await api.patch(`/tasks/${taskId}/status`, { status: newStatus });
    setTask({ ...task, status: newStatus });
    setForm({ ...form, status: newStatus });
  };

  const saveSubtask = async () => {
    if (!subtaskForm.title) return;
    if (editingSubtask) {
      await api.put(`/tasks/${editingSubtask}`, { ...subtaskForm, parentTaskId: Number(taskId), projectId: task.projectId });
      setEditingSubtask(null);
    } else {
      await api.post(`/tasks/${taskId}/subtasks`, subtaskForm);
    }
    setShowSubtaskForm(false);
    setSubtaskForm({ title: '', assigneeId: '', assigneeUsername: '', assigneeName: '', priority: 'MEDIUM', dueDate: '' });
    loadTask();
  };

  const toggleSubtaskDone = async (s) => {
    await api.patch(`/tasks/${s.id}/status`, { status: s.status === 'DONE' ? 'TODO' : 'DONE' });
    loadTask();
  };

  const deleteSubtask = async (s) => {
    if (!window.confirm('Delete this subtask?')) return;
    await api.delete(`/tasks/${s.id}`);
    loadTask();
  };

  const openEditSubtask = (s) => {
    setSubtaskForm({ title: s.title, assigneeId: s.assigneeId || '', assigneeUsername: s.assigneeUsername || '', assigneeName: s.assigneeName || '', priority: s.priority, dueDate: s.dueDate || '' });
    setEditingSubtask(s.id);
    setShowSubtaskForm(true);
  };

  const getMsName = (msId) => { const m = milestones.find(m => m.id === msId); return m ? m.milestoneName : ''; };

  if (!task) return <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading...</div>;

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canDeleteTask = isAdmin() || task.createdBy === user.username;

  const subDone = subtasks.filter(s => s.status === 'DONE').length;
  const subTotal = subtasks.length;
  const subPct = subTotal > 0 ? Math.round((subDone / subTotal) * 100) : 0;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, fontSize: 13, color: '#6b7280' }}>
        <span onClick={() => navigate('/')} style={{ cursor: 'pointer', color: '#3b82f6' }}>Dashboard</span>
        <span>/</span>
        {project && <span onClick={() => navigate(`/projects/${project.id}`)} style={{ cursor: 'pointer', color: '#3b82f6' }}>{project.projectName}</span>}
        <span>/</span>
        <span style={{ color: '#374151', fontWeight: 600 }}>{task.taskCode}</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
        {/* Left Column - Main Content */}
        <div>
          {/* Task Header */}
          <div className="card" style={{ borderLeft: `4px solid ${statusBorderColor[task.status]}`, marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4 }}>{task.taskCode}</div>
                {editing ? (
                  <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={{ fontSize: 20, fontWeight: 700, width: '100%', border: '1px solid #e5e7eb', borderRadius: 6, padding: '6px 10px' }} />
                ) : (
                  <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{task.title}</h2>
                )}
              </div>
              {isDeveloper() && (
                <div style={{ display: 'flex', gap: 6 }}>
                  {editing ? (
                    <>
                      <button className="btn-primary" onClick={saveTask} style={{ fontSize: 12, padding: '6px 14px' }}>Save</button>
                      <button className="btn-ghost" onClick={() => { setEditing(false); setForm(task); }} style={{ fontSize: 12, padding: '6px 14px' }}>Cancel</button>
                    </>
                  ) : (
                    <button className="btn-ghost" onClick={() => setEditing(true)} style={{ fontSize: 12, padding: '6px 14px' }}>Edit</button>
                  )}
                </div>
              )}
            </div>

            {/* Status Quick Change */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
              {TASK_STATUSES.map(s => (
                <button key={s} onClick={() => updateStatus(s)} style={{
                  padding: '5px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                  background: task.status === s ? statusBorderColor[s] : '#f3f4f6',
                  color: task.status === s ? '#fff' : '#6b7280',
                  border: task.status === s ? 'none' : '1px solid #e5e7eb',
                  transition: 'all 0.15s'
                }}>{statusLabel[s]}</button>
              ))}
            </div>

            {/* Description */}
            <div style={{ marginBottom: 4 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: 6 }}>Description</label>
              {editing ? (
                <textarea rows={4} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Add a description..." style={{ width: '100%' }} />
              ) : (
                <div style={{ fontSize: 14, color: task.description ? '#374151' : '#9ca3af', lineHeight: 1.6, minHeight: 40 }}>
                  {task.description || 'No description added.'}
                </div>
              )}
            </div>
          </div>

          {/* Subtasks Section */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div>
                <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 2 }}>Subtasks</h3>
                {subTotal > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <div style={{ width: 120, height: 6, background: '#e5e7eb', borderRadius: 3 }}>
                      <div style={{ height: '100%', width: `${subPct}%`, background: subPct === 100 ? '#059669' : '#3b82f6', borderRadius: 3, transition: 'width 0.3s' }} />
                    </div>
                    <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{subDone}/{subTotal} done ({subPct}%)</span>
                  </div>
                )}
              </div>
              {isDeveloper() && (
                <button className="btn-primary" onClick={() => { setShowSubtaskForm(true); setEditingSubtask(null); setSubtaskForm({ title: '', assigneeId: '', assigneeUsername: '', assigneeName: '', priority: 'MEDIUM', dueDate: '' }); }} style={{ fontSize: 12, padding: '6px 14px' }}>+ Add Subtask</button>
              )}
            </div>

            {/* Add/Edit Subtask Form */}
            {showSubtaskForm && (
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: 14, marginBottom: 14, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 3 }}>Title *</label>
                    <input value={subtaskForm.title} onChange={e => setSubtaskForm({ ...subtaskForm, title: e.target.value })} placeholder="Subtask title" style={{ fontSize: 13 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 3 }}>Assignee</label>
                    <select value={subtaskForm.assigneeId} onChange={e => {
                      const uid = e.target.value;
                      if (!uid) { setSubtaskForm({ ...subtaskForm, assigneeId: '', assigneeUsername: '', assigneeName: '' }); return; }
                      const m = projectMembers.find(m => m.userId === Number(uid));
                      if (m) setSubtaskForm({ ...subtaskForm, assigneeId: m.userId, assigneeUsername: m.username, assigneeName: m.fullName });
                    }} style={{ fontSize: 12 }}>
                      <option value="">Unassigned</option>
                      {projectMembers.map(m => <option key={m.userId} value={m.userId}>{m.fullName}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 3 }}>Priority</label>
                    <select value={subtaskForm.priority} onChange={e => setSubtaskForm({ ...subtaskForm, priority: e.target.value })} style={{ fontSize: 12 }}>
                      {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 3 }}>Due Date</label>
                    <input type="date" value={subtaskForm.dueDate} onChange={e => setSubtaskForm({ ...subtaskForm, dueDate: e.target.value })} style={{ fontSize: 12 }} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
                  <button className="btn-primary" onClick={saveSubtask} style={{ fontSize: 12, padding: '6px 14px' }}>{editingSubtask ? 'Update' : 'Add'}</button>
                  <button className="btn-ghost" onClick={() => { setShowSubtaskForm(false); setEditingSubtask(null); }} style={{ fontSize: 12, padding: '6px 14px' }}>Cancel</button>
                </div>
              </div>
            )}

            {/* Subtask List */}
            {subtasks.length === 0 && !showSubtaskForm ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>No subtasks yet</div>
            ) : (
              <div style={{ display: 'grid', gap: 4 }}>
                {subtasks.map(s => (
                  <div key={s.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    background: s.status === 'DONE' ? '#f0fdf4' : '#fff',
                    borderRadius: 8, border: '1px solid #f0f0f0', transition: 'background 0.15s'
                  }}>
                    <input type="checkbox" checked={s.status === 'DONE'} onChange={() => toggleSubtaskDone(s)}
                      style={{ width: 18, height: 18, cursor: 'pointer', accentColor: '#059669', flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: 13, fontWeight: 500,
                        color: s.status === 'DONE' ? '#9ca3af' : '#0f172a',
                        textDecoration: s.status === 'DONE' ? 'line-through' : 'none'
                      }}>{s.title}</div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 3, alignItems: 'center' }}>
                        <span style={{ fontSize: 10, color: '#9ca3af' }}>{s.taskCode}</span>
                        {s.dueDate && (
                          <span style={{ fontSize: 10, color: new Date(s.dueDate) < new Date() && s.status !== 'DONE' ? '#dc2626' : '#9ca3af' }}>📅 {s.dueDate}</span>
                        )}
                      </div>
                    </div>
                    {s.assigneeName && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Avatar name={s.assigneeName} size={22} fontSize={9} />
                        <span style={{ fontSize: 11, color: '#6b7280' }}>{s.assigneeName}</span>
                      </div>
                    )}
                    <span className={`badge ${priorityColor(s.priority)}`} style={{ fontSize: 10 }}>{s.priority}</span>
                    <span className={`badge ${statusColor(s.status)}`} style={{ fontSize: 10 }}>{s.status.replace(/_/g, ' ')}</span>
                    {isDeveloper() && (
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button className="btn-ghost" onClick={() => openEditSubtask(s)} style={{ padding: '2px 6px', fontSize: 11 }}>Edit</button>
                        {(isAdmin() || s.createdBy === user.username) && (
                          <button className="btn-ghost" onClick={() => deleteSubtask(s)} style={{ padding: '2px 6px', fontSize: 11, color: '#dc2626' }}>Del</button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Attachments Section */}
          <div className="card" style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Attachments ({attachments.length})</h3>
              {isDeveloper() && (
                <div>
                  <input type="file" ref={fileInputRef} onChange={uploadFile} style={{ display: 'none' }} />
                  <button className="btn-primary" onClick={() => fileInputRef.current?.click()} disabled={uploading} style={{ fontSize: 12, padding: '6px 14px' }}>
                    {uploading ? 'Uploading...' : '+ Upload'}
                  </button>
                </div>
              )}
            </div>
            {attachments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#9ca3af', fontSize: 13 }}>No attachments yet</div>
            ) : (
              <div style={{ display: 'grid', gap: 4 }}>
                {attachments.map(att => (
                  <div key={att.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    background: '#f9fafb', borderRadius: 8, border: '1px solid #f0f0f0'
                  }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{getFileIcon(att.fileName)}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{att.fileName}</div>
                      <div style={{ fontSize: 10, color: '#9ca3af' }}>
                        {formatFileSize(att.fileSize)} · {att.createdBy} · {new Date(att.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                    <button className="btn-ghost" onClick={() => downloadAttachment(att)} style={{ padding: '3px 8px', fontSize: 11, color: '#3b82f6' }}>View</button>
                    {(isAdmin() || att.createdBy === user.username) && (
                      <button className="btn-ghost" onClick={() => deleteAttachment(att)} style={{ padding: '3px 8px', fontSize: 11, color: '#dc2626' }}>Del</button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Details Sidebar */}
        <div>
          <div className="card" style={{ position: 'sticky', top: 24 }}>
            <h4 style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 16, paddingBottom: 10, borderBottom: '1px solid #e5e7eb' }}>Details</h4>

            <div style={{ display: 'grid', gap: 14 }}>
              {/* Status */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Status</label>
                <span className={`badge ${statusColor(task.status)}`}>{statusLabel[task.status]}</span>
              </div>

              {/* Priority */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Priority</label>
                {editing ? (
                  <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} style={{ fontSize: 12 }}>
                    {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                ) : (
                  <span className={`badge ${priorityColor(task.priority)}`}>{task.priority}</span>
                )}
              </div>

              {/* Assignee */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Assignee</label>
                {editing ? (
                  <select value={form.assigneeId || ''} onChange={e => {
                    const uid = e.target.value;
                    if (!uid) { setForm({ ...form, assigneeId: null, assigneeUsername: '', assigneeName: '' }); return; }
                    const m = projectMembers.find(m => m.userId === Number(uid));
                    if (m) setForm({ ...form, assigneeId: m.userId, assigneeUsername: m.username, assigneeName: m.fullName });
                  }} style={{ fontSize: 12 }}>
                    <option value="">Unassigned</option>
                    {projectMembers.map(m => <option key={m.userId} value={m.userId}>{m.fullName}</option>)}
                  </select>
                ) : task.assigneeName ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Avatar name={task.assigneeName} size={28} fontSize={12} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{task.assigneeName}</div>
                      <div style={{ fontSize: 11, color: '#9ca3af' }}>{task.assigneeUsername}</div>
                    </div>
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: '#9ca3af' }}>Unassigned</span>
                )}
              </div>

              {/* Milestone */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Milestone</label>
                {editing ? (
                  <select value={form.milestoneId || ''} onChange={e => setForm({ ...form, milestoneId: e.target.value || null })} style={{ fontSize: 12 }}>
                    <option value="">None</option>
                    {milestones.map(m => <option key={m.id} value={m.id}>{m.milestoneName}</option>)}
                  </select>
                ) : (
                  <span style={{ fontSize: 13, color: task.milestoneId ? '#8b5cf6' : '#9ca3af' }}>
                    {task.milestoneId ? `🎯 ${getMsName(task.milestoneId)}` : 'None'}
                  </span>
                )}
              </div>

              {/* Start Date */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Start Date</label>
                {editing ? (
                  <input type="date" value={form.startDate || ''} onChange={e => setForm({ ...form, startDate: e.target.value })} style={{ fontSize: 12 }} />
                ) : (
                  <span style={{ fontSize: 13, color: task.startDate ? '#374151' : '#9ca3af' }}>
                    {task.startDate ? `📅 ${task.startDate}` : 'Not set'}
                  </span>
                )}
              </div>

              {/* Due Date */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Due Date</label>
                {editing ? (
                  <input type="date" value={form.dueDate || ''} onChange={e => setForm({ ...form, dueDate: e.target.value })} style={{ fontSize: 12 }} />
                ) : (
                  <span style={{
                    fontSize: 13,
                    color: task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'DONE' ? '#dc2626' : task.dueDate ? '#374151' : '#9ca3af',
                    fontWeight: task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'DONE' ? 600 : 400
                  }}>
                    {task.dueDate ? `📅 ${task.dueDate}` : 'Not set'}
                  </span>
                )}
              </div>

              {/* Estimated Hours */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Estimated Hours</label>
                {editing ? (
                  <input type="number" value={form.estimatedHours || ''} onChange={e => setForm({ ...form, estimatedHours: e.target.value })} style={{ fontSize: 12 }} placeholder="0" />
                ) : (
                  <span style={{ fontSize: 13, color: task.estimatedHours ? '#374151' : '#9ca3af' }}>
                    {task.estimatedHours ? `${task.estimatedHours}h` : 'Not set'}
                  </span>
                )}
              </div>

              {/* Actual Hours */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Actual Hours</label>
                {editing ? (
                  <input type="number" value={form.actualHours || ''} onChange={e => setForm({ ...form, actualHours: e.target.value })} style={{ fontSize: 12 }} placeholder="0" />
                ) : (
                  <span style={{ fontSize: 13, color: task.actualHours ? '#374151' : '#9ca3af' }}>
                    {task.actualHours ? `${task.actualHours}h` : 'Not set'}
                  </span>
                )}
              </div>

              {/* Project */}
              {project && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', display: 'block', marginBottom: 4 }}>Project</label>
                  <span onClick={() => navigate(`/projects/${project.id}`)} style={{ fontSize: 13, color: '#3b82f6', cursor: 'pointer', fontWeight: 500 }}>
                    {project.projectCode} — {project.projectName}
                  </span>
                </div>
              )}

              {/* Timestamps */}
              <div style={{ paddingTop: 10, borderTop: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: 10, color: '#9ca3af', marginBottom: 2 }}>Created: {task.createdAt ? new Date(task.createdAt).toLocaleString() : '—'}</div>
                <div style={{ fontSize: 10, color: '#9ca3af' }}>Updated: {task.updatedAt ? new Date(task.updatedAt).toLocaleString() : '—'}</div>
              </div>
            </div>

            {/* Delete */}
            {canDeleteTask && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #e5e7eb' }}>
                <button className="btn-danger" onClick={async () => {
                  if (!window.confirm('Delete this task and all subtasks?')) return;
                  try {
                    await api.delete(`/tasks/${taskId}`);
                    navigate(`/projects/${task.projectId}`);
                  } catch (e) { alert(e.response?.data?.error || 'Failed to delete'); }
                }} style={{ width: '100%', fontSize: 12 }}>Delete Task</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
